import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ArrowRight, ChevronLeft, ChevronRight, Search, Star, Users, Shield } from 'lucide-react';
import SearchModal from './SearchModal';
import useUserLocation from '../../hooks/useUserLocation';
import { siteSettingsService } from '../../services/siteSettingsService';

const DEFAULT_HERO_IMAGE = '/images/01_-_HERO_1.webp';

const HeroSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [location, setLocation] = useState('');
  const [currentTextIndex, setCurrentTextIndex] = useState(0);
  const backgroundRefs = useRef([]);
  const slideIntervalRef = useRef(null);
  const textIntervalRef = useRef(null);

  const { data: settingsResponse } = useQuery({
    queryKey: ['siteSettings'],
    queryFn: () => siteSettingsService.get(),
    staleTime: 5 * 60 * 1000,
  });

  const { userLocation } = useUserLocation();

  useEffect(() => {
    const locationString =
      userLocation?.display || userLocation?.address || userLocation?.country || '';
    if (locationString) {
      setLocation(locationString);
    }
  }, [userLocation?.display, userLocation?.address, userLocation?.country]);

  const searchTexts = [
    'Find vendors for your event',
    'Discover photo booth services',
    'Book entertainment packages',
    'Connect with top vendors',
    'Plan your perfect event',
  ];

  const heroSlides = useMemo(() => {
    const customImage = settingsResponse?.data?.home_hero_image;
    return [
      {
        image: customImage || DEFAULT_HERO_IMAGE,
        heading: 'Your Dream Event Starts Here',
        subheading: 'Starts Here',
        description: 'Connect with verified vendors and bring your vision to life',
      },
    ];
  }, [settingsResponse?.data?.home_hero_image]);

  useEffect(() => {
    setIsVisible(true);

    const startAutoSlide = () => {
      slideIntervalRef.current = setInterval(() => {
        setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
      }, 5000);
    };

    const startTextRotation = () => {
      textIntervalRef.current = setInterval(() => {
        setCurrentTextIndex((prev) => (prev + 1) % searchTexts.length);
      }, 3000);
    };

    startAutoSlide();
    startTextRotation();

    return () => {
      if (slideIntervalRef.current) {
        clearInterval(slideIntervalRef.current);
      }
      if (textIntervalRef.current) {
        clearInterval(textIntervalRef.current);
      }
    };
  }, [heroSlides.length, searchTexts.length]);

  useEffect(() => {
    let rafId = null;
    let ticking = false;

    const handleScroll = () => {
      if (!ticking) {
        ticking = true;
        rafId = requestAnimationFrame(() => {
          backgroundRefs.current.forEach((ref) => {
            if (ref) {
              const scrolled = window.scrollY;
              const rate = scrolled * 0.5;
              ref.style.transform = `translateX(-50%) translateY(${rate}px)`;
            }
          });
          ticking = false;
        });
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (rafId) {
        cancelAnimationFrame(rafId);
      }
    };
  }, []);

  const goToSlide = (index) => {
    setCurrentSlide(index);
    if (slideIntervalRef.current) {
      clearInterval(slideIntervalRef.current);
    }
    slideIntervalRef.current = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
    }, 5000);
  };

  const nextSlide = () => {
    goToSlide((currentSlide + 1) % heroSlides.length);
  };

  const prevSlide = () => {
    goToSlide((currentSlide - 1 + heroSlides.length) % heroSlides.length);
  };

  const currentSlideData = heroSlides[currentSlide];

  return (
    <section
      className="relative min-h-screen flex items-center justify-center text-white overflow-hidden"
      style={{ marginTop: '-64px', paddingTop: '64px', position: 'relative' }}
    >
      {heroSlides.map((slide, index) => (
        <div
          key={`${slide.image}-${index}`}
          ref={(el) => (backgroundRefs.current[index] = el)}
          className={`absolute w-screen transition-opacity duration-1000 ${
            index === currentSlide ? 'opacity-100 z-0' : 'opacity-0 z-[-1]'
          }`}
          style={{
            top: '-64px',
            left: '50%',
            transform: 'translateX(-50%) translateY(0px)',
            height: 'calc(100vh + 64px)',
            minHeight: '600px',
            willChange: 'transform',
          }}
        >
          <img
            src={slide.image}
            alt={`Hero background ${index + 1}`}
            className="w-full h-full object-cover object-center"
            style={{
              minHeight: '600px',
              height: '100%',
              width: '100%',
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/50 to-black/70"></div>
        </div>
      ))}

      {heroSlides.length > 1 && (
        <>
          <button
            onClick={prevSlide}
            className="absolute left-4 top-1/2 -translate-y-1/2 z-30 bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-full p-2 transition-all duration-300 hidden sm:flex items-center justify-center"
            aria-label="Previous slide"
          >
            <ChevronLeft className="w-6 h-6 text-white" />
          </button>
          <button
            onClick={nextSlide}
            className="absolute right-4 top-1/2 -translate-y-1/2 z-30 bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-full p-2 transition-all duration-300 hidden sm:flex items-center justify-center"
            aria-label="Next slide"
          >
            <ChevronRight className="w-6 h-6 text-white" />
          </button>

          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-30 flex space-x-2">
            {heroSlides.map((_, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                className={`h-2 rounded-full transition-all duration-300 ${
                  index === currentSlide ? 'w-8 bg-white' : 'w-2 bg-white/50 hover:bg-white/75'
                }`}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>
        </>
      )}

      <div className="relative z-20 w-full max-w-5xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
        <div
          className={`transform transition-all duration-1000 ${
            isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
          }`}
        >
          <div className="text-center mb-6 sm:mb-8">
            <div className="inline-flex items-center gap-2 bg-white/15 backdrop-blur-sm text-white px-3 py-1.5 rounded-full text-xs font-light mb-4 border border-white/20">
              <span className="w-1.5 h-1.5 bg-green-400 rounded-full animate-pulse"></span>
              <span>Trusted by 10,000+ Event Planners</span>
            </div>

            <h1
              key={currentSlide}
              className="text-3xl sm:text-4xl md:text-5xl font-bold mb-2 text-center leading-tight text-white drop-shadow-2xl animate-fade-in"
            >
              {currentSlideData.heading}
            </h1>

            <p className="text-sm sm:text-base md:text-lg font-light text-white/80 mb-6 max-w-xl mx-auto drop-shadow-lg">
              {currentSlideData.description}
            </p>
          </div>

          <div
            className={`max-w-2xl mx-auto mb-8 transform transition-all duration-1000 delay-300 ${
              isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
            }`}
          >
            <button
              onClick={() => setShowSearchModal(true)}
              className="w-full bg-white rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 p-4 md:p-5 group relative overflow-hidden"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-gray-50 to-white opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

              <div className="relative flex items-center justify-between">
                <div className="flex items-center gap-3 flex-1">
                  <div className="bg-gradient-to-br from-gray-900 to-gray-700 p-3 rounded-xl group-hover:scale-105 group-hover:rotate-2 transition-all duration-300 shadow-md">
                    <Search className="w-5 h-5 text-white" />
                  </div>
                  <div className="text-left overflow-hidden flex-1">
                    <p className="text-xs font-light text-gray-400 mb-1 uppercase tracking-wide">
                      Start your search
                    </p>
                    <div className="relative h-6 overflow-hidden">
                      {searchQuery || location ? (
                        <p className="text-base font-normal text-gray-900">
                          {searchQuery
                            ? `${searchQuery}${location ? ` in ${location}` : ''}`
                            : location
                              ? `Connect with top vendors in ${location}`
                              : 'Any service'}
                        </p>
                      ) : (
                        <p
                          key={currentTextIndex}
                          className="text-sm font-normal text-gray-900 animate-slide-up-text"
                        >
                          {searchTexts[currentTextIndex]}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
                <div className="bg-gray-900 p-2.5 rounded-full group-hover:bg-gray-800 transition-all duration-300">
                  <ArrowRight className="w-4 h-4 text-white group-hover:translate-x-1 transition-transform duration-300" />
                </div>
              </div>
            </button>
          </div>

          <div
            className={`max-w-3xl mx-auto transform transition-all duration-1000 delay-500 ${
              isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
            }`}
          >
            <div className="flex flex-wrap items-center justify-center gap-4 md:gap-6">
              <div className="flex items-center gap-2 text-white/90">
                <div className="bg-white/15 backdrop-blur-sm p-2 rounded-lg">
                  <Star className="w-4 h-4 text-yellow-400 fill-current" />
                </div>
                <div className="text-left">
                  <p className="text-xs font-light">4.8/5 Rating</p>
                  <p className="text-xs text-white/60">2000+ reviews</p>
                </div>
              </div>

              <div className="hidden sm:block w-px h-8 bg-white/20"></div>

              <div className="flex items-center gap-2 text-white/90">
                <div className="bg-white/15 backdrop-blur-sm p-2 rounded-lg">
                  <Users className="w-4 h-4 text-blue-400" />
                </div>
                <div className="text-left">
                  <p className="text-xs font-light">500+ Vendors</p>
                  <p className="text-xs text-white/60">Verified</p>
                </div>
              </div>

              <div className="hidden sm:block w-px h-8 bg-white/20"></div>

              <div className="flex items-center gap-2 text-white/90">
                <div className="bg-white/15 backdrop-blur-sm p-2 rounded-lg">
                  <Shield className="w-4 h-4 text-green-400" />
                </div>
                <div className="text-left">
                  <p className="text-xs font-light">Secure Booking</p>
                  <p className="text-xs text-white/60">Protected</p>
                </div>
              </div>
            </div>
          </div>

          <SearchModal
            isOpen={showSearchModal}
            onClose={() => setShowSearchModal(false)}
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            location={location}
            setLocation={setLocation}
          />
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
